import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Content,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  TransformWrapper,
  TransformComponent,
} from 'react-zoom-pan-pinch';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { Box, Typography } from '@mui/material';

const subgraphClasses: Record<string, { className: string; fill: string }> = {
  Component: { className: 'subgraph_Component', fill: '#333a' },
  API: { className: 'subgraph_API', fill: '#444a' },
  System: { className: 'subgraph_System', fill: '#2e2e2e' },
  Location: { className: 'subgraph_Location', fill: '#3b3b3b' },
  Resource: { className: 'subgraph_Resource', fill: '#3f3f3f' },
  Group: { className: 'subgraph_Group', fill: '#2b2b2b' },
  User: { className: 'subgraph_User', fill: '#1f1f1f' },
};

const kindStyles: Record<string, { fill: string; stroke: string }> = {
  Component: { fill: '#455a64', stroke: '#90caf9' },
  API: { fill: '#5d4037', stroke: '#ffb74d' },
  System: { fill: '#2e7d32', stroke: '#81c784' },
  Resource: { fill: '#6a1b9a', stroke: '#ce93d8' },
  Location: { fill: '#4527a0', stroke: '#b39ddb' },
  Group: { fill: '#bf360c', stroke: '#ffab91' },
  User: { fill: '#558b2f', stroke: '#dcedc8' },
  Default: { fill: '#37474f', stroke: '#cfd8dc' },
};

function hasTarget(rel: any): rel is { target: { kind: string; name: string } } {
  return rel && typeof rel.target?.kind === 'string' && typeof rel.target?.name === 'string';
}

export const MermaidDiagramPage = () => {
  const diagramRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<ReactZoomPanPinchRef | null>(null);
  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const allEntities = await catalogApi.getEntities();
        setEntities(allEntities.items);
      } catch (err) {
        console.error('Erro ao carregar entidades:', err);
        if (err instanceof Error) setError(err); else setError(new Error('Erro desconhecido'));
      } finally {
        setLoading(false);
      }
    };
    fetchEntities();
  }, [catalogApi]);

  useEffect(() => {
    const renderMermaid = async () => {
      if (!diagramRef.current || !entities.length) return;
      const diagram = generateMermaidFromEntities(entities);
      try {
        mermaid.initialize({
          startOnLoad: false,
          theme: 'dark',
          flowchart: {
            useMaxWidth: false,
            htmlLabels: true,
            curve: 'monotoneX',
            nodeSpacing: 60,
            rankSpacing: 100,
          },
        });
        diagramRef.current.className = 'mermaid';
        diagramRef.current.innerHTML = diagram;
        await mermaid.run({ nodes: [diagramRef.current] });
        const svg = diagramRef.current.querySelector('svg');
        if (svg) {
          svg.style.backgroundColor = '#121212';
          svg.style.fontSize = '14px';
        }
      } catch (err) {
        setError(new Error(`Erro ao renderizar diagrama: ${err instanceof Error ? err.message : JSON.stringify(err)}`));
      }
    };
    renderMermaid();
  }, [entities]);

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const usedRelationTypes = new Set<string>();
  entities.forEach(entity => {
    entity.relations?.forEach(rel => {
      if (rel?.type) usedRelationTypes.add(rel.type);
    });
  });

  const relationLegend = [
    { label: 'dependsOn', color: '#00bcd4' },
    { label: 'consumesApi', color: '#4dd0e1' },
    { label: 'providesApi', color: '#81c784' },
    { label: 'ownedBy', color: '#ff6d00' },
    { label: 'partOf', color: '#d50000' },
    { label: 'memberOf', color: '#9575cd' },
    { label: 'parentOf', color: '#64b5f6' },
    { label: 'manages', color: '#ffd54f' },
    { label: 'relatedTo', color: '#90a4ae' },
    { label: 'supports', color: '#4caf50' },
    { label: 'implements', color: '#f06292' },
    { label: 'connectsTo', color: '#009688' },
    { label: 'dependsUpon', color: '#ba68c8' },
    { label: 'runsOn', color: '#ffcc80' },
    { label: 'hostedBy', color: '#90caf9' },
    { label: 'ownedByGroup', color: '#e57373' },
    { label: 'delegatedTo', color: '#a1887f' },
  ];

  const filteredLegend = relationLegend.filter(rel => usedRelationTypes.has(rel.label));

  return (
    <Page themeId="home">
      <Box sx={{ overflow: 'hidden', height: '100vh', width: '100vw', p: 0, m: 0, backgroundColor: '#121212' }}>
        <Content>
          <Typography variant="h4" gutterBottom align="center" sx={{ color: '#fff', padding: 2 }}>
            Diagrama Engenharia Clinicorp
          </Typography>

          <Box display="flex" flexDirection="row">
            {/* Sidebar esquerda */}
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2} px={2} pb={2} minWidth="240px">
              <Box display="flex" flexDirection="column" gap={2}>
                <button
                  onClick={() => {
                    zoomRef.current?.setTransform(0, 0, 1, 300);
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  Resetar Zoom
                </button>

                <button
                onClick={() => {
                    const svg = diagramRef.current?.querySelector('svg');
                    if (!svg) return;
                
                    const serializer = new XMLSerializer();
                    const clonedSvg = svg.cloneNode(true) as SVGSVGElement;
                
                    // Define escala de exportação
                    const scale = 0.8;
                
                    // Calcula tamanho proporcional
                    const originalWidth = svg.viewBox.baseVal.width || svg.getBoundingClientRect().width;
                    const originalHeight = svg.viewBox.baseVal.height || svg.getBoundingClientRect().height;
                
                    const exportWidth = originalWidth * scale;
                    const exportHeight = originalHeight * scale;
                
                    // Aplica dimensões
                    clonedSvg.setAttribute('width', `${exportWidth}`);
                    clonedSvg.setAttribute('height', `${exportHeight}`);
                    clonedSvg.setAttribute('viewBox', `0 0 ${originalWidth} ${originalHeight}`);
                
                    // Aplica transformação de escala visual
                    clonedSvg.innerHTML = `<g transform="scale(${scale})">${clonedSvg.innerHTML}</g>`;
                
                    // Serializa e exporta
                    const svgString = serializer.serializeToString(clonedSvg);
                    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'diagrama-mermaid.svg';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: '#388e3c',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  Exportar Diagrama
                </button>
              </Box>

              <Box display="flex" flexDirection="column" gap={1}>
                {filteredLegend.map(rel => (
                  <Box key={rel.label} display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: rel.color, borderRadius: '50%' }} />
                    <Typography variant="body2" color="white">{rel.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Diagrama */}
            <TransformWrapper
              ref={zoomRef}
              initialScale={1.0}
              minScale={0.1}
              maxScale={20}
              centerOnInit={true}
              limitToBounds={false}
              wheel={{ step: 0.1 }}
              doubleClick={{ disabled: true }}
            >
              <TransformComponent
                wrapperStyle={{
                  marginLeft: '8px',
                  borderLeft: '1px solid #333',
                  paddingLeft: '8px',
                  width: '100%',
                  height: 'calc(100vh - 240px)',
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#121212',
                }}
              >
                <Box
                  ref={diagramRef}
                  className="mermaid"
                  sx={{
                    width: 'auto',
                    height: 'auto',
                    overflow: 'visible',
                    backgroundColor: '#121212',
                    padding: 2,
                  }}
                />
              </TransformComponent>
            </TransformWrapper>
          </Box>
        </Content>
      </Box>
    </Page>
  );
};

function generateMermaidFromEntities(entities: Entity[]) {
  let diagram = 'graph TB\n\n';
  const id = (e: Entity) =>
    `${e.kind}_${e.metadata.name}`.replace(/[^a-zA-Z0-9_]/g, '_');

  const kindMap: Record<string, Entity[]> = {};
  for (const entity of entities) {
    const kind = entity.kind;
    if (!kindMap[kind]) kindMap[kind] = [];
    kindMap[kind].push(entity);
  }

  const subgraphClassMap: string[] = [];

  const preferredOrder = [
    'Component',
    'Location',
    'API',
    'System',
    'Resource',
    'Group',
    'User',
  ];

  for (const kind of preferredOrder) {
    if (!kindMap[kind]) continue;

    const subgraphId = kind;
    const subgraphClass = subgraphClasses[kind]?.className ?? '';
    if (subgraphClass) subgraphClassMap.push(`class ${subgraphId} ${subgraphClass};`);

    diagram += `subgraph ${subgraphId}\n`;
    for (const entity of kindMap[kind]) {
      const nodeId = id(entity);
      diagram += `  ${nodeId}["${entity.metadata.name}"]\n`;
    }
    diagram += 'end\n\n';
  }

  let linkIndex = 0;
  for (const entity of entities) {
    const sourceId = id(entity);
    if (entity.relations?.length) {
      for (const rel of entity.relations) {
        if (hasTarget(rel)) {
          const targetKind = rel.target.kind;
          const targetName = rel.target.name;
          const targetId = `${targetKind}_${targetName}`.replace(/[^a-zA-Z0-9_]/g, '_');
          const relLabel = rel.type.replace(/([a-z])([A-Z])/g, '$1\\n$2');
          diagram += `${sourceId} -->|${relLabel}| ${targetId}\n`;

          const strokeColor = {
            dependsOn: '#00bcd4',
            consumesApi: '#4dd0e1',
            providesApi: '#81c784',
            ownedBy: '#ff6d00',
            partOf: '#d50000',
            memberOf: '#9575cd',
            parentOf: '#64b5f6',
            manages: '#ffd54f',
            relatedTo: '#90a4ae',
            supports: '#4caf50',
            implements: '#f06292',
            connectsTo: '#009688',
            dependsUpon: '#ba68c8',
            runsOn: '#ffcc80',
            hostedBy: '#90caf9',
            ownedByGroup: '#e57373',
            delegatedTo: '#a1887f'
          }[rel.type] || '#aaa';

          diagram += `linkStyle ${linkIndex} stroke:${strokeColor},stroke-width:3px;\n`;
          linkIndex++;
        }
      }
    }
  }

  for (const entity of entities) {
    const eid = id(entity);
    const style = kindStyles[entity.kind] || kindStyles.Default;
    diagram += `style ${eid} fill:${style.fill},stroke:${style.stroke},stroke-width:2.5px;\n`;
  }

  diagram += `classDef defaultNodeStyle fill:#1e1e1e,stroke:#888,stroke-width:2.5px,font-size:30px,padding:20px 40px;\n`;


  for (const kind in subgraphClasses) {
    const { className, fill } = subgraphClasses[kind];
    diagram += `classDef ${className} fill:${fill},stroke:#666,stroke-width:1.5px;\n`;
  }

  diagram += subgraphClassMap.join('\n') + '\n';

  return diagram;
}
